import React from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { SalesforceRef } from '../../../support/assessments';

/**
 * Presentational server-side typeahead for a Salesforce record (Account or Opportunity).
 *
 * Kept dumb on purpose: the account and opportunity variants each own their own search hook and feed
 * this the current `options`, `loading` and term. Search is server-side so `filterOptions` is the
 * identity — MUI must not also filter the already-filtered list. The committed value is a
 * `{id, name}` ref, which is exactly what the assessment answer stores.
 */

type Props = {
  label: string;
  placeholder?: string;
  value: SalesforceRef | null;
  onChange: (value: SalesforceRef | null) => void;
  term: string;
  onTermChange: (term: string) => void;
  options: SalesforceRef[];
  loading: boolean;
  disabled?: boolean;
};

const SalesforceLookupAutocomplete: React.FC<Props> = ({
  label,
  placeholder,
  value,
  onChange,
  term,
  onTermChange,
  options,
  loading,
  disabled,
}) => {
  // Keep the current value selectable even before the user types (the search only runs on input).
  const merged = value && !options.some((o) => o.id === value.id) ? [value, ...options] : options;

  return (
    <Autocomplete<SalesforceRef>
      value={value}
      options={merged}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      inputValue={term}
      // Keep the field's typed text on blur. With the default `clearOnBlur` (true for a non-freeSolo
      // Autocomplete), blurring after an empty result blanks MUI's internal input while the controlled
      // `inputValue={term}` forces the old term back — the two desync, and subsequent keystrokes can
      // stop firing `onInputChange('input')`, so the search never re-runs. Not clearing on blur keeps
      // the controlled term and the field in step.
      clearOnBlur={false}
      onInputChange={(_e, next, reason) => {
        // Drive the search term from every user-initiated text change — typing ('input') AND the
        // clear (✕) button ('clear'). Previously only 'input' propagated, so a 'clear' left `term`
        // (hence the controlled `inputValue`) stale and out of sync with the field, which is the
        // "type a new value but it won't query again" symptom. 'reset' (MUI echoing a selected
        // option's label back) is intentionally ignored so picking a record doesn't re-trigger a
        // search for its own name.
        if (reason === 'input' || reason === 'clear') onTermChange(next);
      }}
      onChange={(_e, next) => {
        onChange(next ? { id: next.id, name: next.name } : null);
        if (!next) onTermChange('');
      }}
      noOptionsText={term ? 'No matches' : 'Type a name or id to search'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SalesforceLookupAutocomplete;
